import ErrorMessage from "components/error-message";
import SuccessMessage from "components/success-message";
import SuccessTag from "components/success-tag/SuccessTag";
import ToggleSwitch from "components/toggle-switch";
import { useTimeOnPageContext } from "contexts/time-on-page";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Spinner, Tab, Tabs } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AmplitudePageName, track } from "utils/amplitude";
import userSettingsQueryOptions from "queries/userSettingsQueryOptions";
import useSaveUserSettingsMutation from "mutations/useSaveUserSettingsMutation";
import { useUser } from "contexts/UserContext";
import styles from "./Telehealth.module.scss";
import { RECORD_TELEHEALTH_AUDIO } from "constants/featureFlags";

const AMPLITUDE_PAGE_NAME: AmplitudePageName = "pro-settings-teleheatlh";

const TELEHEALTH_SETTING_OPTIONS = [
  { value: "telehealth", label: "Session Control" },
];

const TelehealthSetting = () => {
  const { setPage } = useTimeOnPageContext();
  useEffect(() => setPage(AMPLITUDE_PAGE_NAME), [setPage]);

  const copyLinkTarget = useRef(null);
  const [copied, setCopied] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const { activeTab } = useMemo(() => {
    const query = new URLSearchParams(location.search);
    return {
      activeTab: query.get("tab") || "",
    };
  }, [location.search]);

  const [tab, setTab] = useState(activeTab || "telehealth");
  const [changed, setChanged] = useState(false);

  const { user } = useUser();
  const userSettingsQuery = useQuery({
    ...userSettingsQueryOptions({ userId: user?.sub || "" }),
    enabled: !!user?.sub,
  });
  const updateUserSettingsMutation = useSaveUserSettingsMutation({
    userId: user?.sub || "",
  });

  const [waitingRoomEnabled, setWaitingRoomEnabled] = useState<boolean>(
    userSettingsQuery.data?.telehealth?.waitingRoomEnabled ?? false,
  );
  const [recordTelehealthAudio, setRecordTelehealthAudio] = useState<boolean>(
    userSettingsQuery.data?.telehealth?.recordTelehealthAudio ?? false,
  );

  useEffect(() => {
    if (userSettingsQuery.data?.telehealth?.waitingRoomEnabled !== undefined) {
      setWaitingRoomEnabled(
        userSettingsQuery.data.telehealth.waitingRoomEnabled,
      );
    }
    if (
      userSettingsQuery.data?.telehealth?.recordTelehealthAudio !== undefined
    ) {
      setRecordTelehealthAudio(
        userSettingsQuery.data.telehealth.recordTelehealthAudio,
      );
    }
  }, [userSettingsQuery.data?.telehealth]);

  const [error, setError] = useState<any>();
  const [success, setSuccess] = useState<string>();
  const [busy, setBusy] = useState(false);

  const saveTelehealthSettings = async () => {
    setBusy(true);
    try {
      const newSettings = {
        audioRecorder: {
          ...userSettingsQuery.data?.audioRecorder,
        },
        telehealth: {
          waitingRoomEnabled: waitingRoomEnabled,
          recordTelehealthAudio: recordTelehealthAudio,
        },
      };
      updateUserSettingsMutation.mutate(newSettings);
      setSuccess("Saved");
    } catch (error) {
      setError(error);
    } finally {
      setBusy(false);
    }
  };

  const onWaitingRoomChange = () => {
    setWaitingRoomEnabled((s) => !s);
    setChanged(true);
  };

  const onRecordTelehealthAudioChange = () => {
    setRecordTelehealthAudio((s) => !s);
    setChanged(true);
  };

  return (
    <div className={styles["telehealth"]}>
      {copied && (
        <SuccessTag
          target={copyLinkTarget.current}
          show={copied}
          onHide={() => setCopied(false)}
          placement={"top-end"}
          successMsg={"Copied"}
          duration={1000}
        />
      )}
      {error && (
        <ErrorMessage
          show={true}
          onHide={() => setError(undefined)}
          errorMsg={error?.toString()}
        />
      )}
      {success && (
        <SuccessMessage
          show={true}
          onHide={() => setSuccess(undefined)}
          message={"Saved"}
          duration={1000}
        />
      )}
      <div className={styles["telehealth__header"]}>
        <h4 className={"rg-32 deep-blue questrial mb-0"}>Telehealth</h4>
        <Tabs
          activeKey={tab}
          onSelect={(k) => {
            setTab((s) => k || s);
            navigate(`/pro-dashboard/settings/telehealth?tab=${k}`, { replace: true });
          }}
          className={styles["telehealth__tabs"]}
        >
          {TELEHEALTH_SETTING_OPTIONS.map((el, i) => (
            <Tab
              key={i}
              eventKey={el.value}
              title={el.label}
              tabClassName={styles["telehealth__tab"]}
              onEnter={() =>
                track(AMPLITUDE_PAGE_NAME, "telehealth-tab", "click", {
                  eventProperties: { tab: el.value },
                })
              }
            ></Tab>
          ))}
        </Tabs>
      </div>

      <div className={styles["telehealth__body"]}>
        {tab === "telehealth" && (
          <>
            <div className={styles["telehealth__section"]}>
              <div className={styles["telehealth__section-title"]}>
                Session Control
              </div>
              <div className={"rg-14 mb-4"}>
                Control access with a waiting room and prevent accidental
                session endings with confirmation prompts.
              </div>

              <div className={styles["telehealth__settings-input"]}>
                <ToggleSwitch
                  checked={waitingRoomEnabled}
                  onChange={onWaitingRoomChange}
                  onClick={() =>
                    track(AMPLITUDE_PAGE_NAME, "waiting-room-toggle", "click")
                  }
                />
                <div className={styles["telehealth__settings-input-label"]}>
                  <span
                    aria-hidden="true"
                    className={styles["telehealth__settings-input-label-title"]}
                  >
                    Use waiting room
                  </span>
                  <span
                    aria-hidden="true"
                    className={
                      styles["telehealth__settings-input-label-description"]
                    }
                  >
                    Manually control when participants can join or leave the
                    session.{" "}
                  </span>
                </div>
              </div>

              {RECORD_TELEHEALTH_AUDIO && (
                <div className={styles["telehealth__settings-input"]}>
                  <ToggleSwitch
                    checked={recordTelehealthAudio}
                    onChange={onRecordTelehealthAudioChange}
                    onClick={() =>
                      track(AMPLITUDE_PAGE_NAME, "waiting-room-toggle", "click")
                    }
                  />
                  <div className={styles["telehealth__settings-input-label"]}>
                    <span
                      aria-hidden="true"
                      className={
                        styles["telehealth__settings-input-label-title"]
                      }
                    >
                      Record telehealth audio
                    </span>
                    <span
                      aria-hidden="true"
                      className={
                        styles["telehealth__settings-input-label-description"]
                      }
                    >
                      Store audio recordings of telehealth sessions.{" "}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <div className={styles["telehealth__footer"]}>
        <div className={"gray01 rg-12"}>
          ©2026 Orchid Exchange Inc. All rights reserved
        </div>
        <Button
          variant="primary"
          onClick={() => {
            track(AMPLITUDE_PAGE_NAME, "save-button", "click");
            saveTelehealthSettings();
          }}
          className={styles["data-analysis__save-button"]}
          disabled={!changed || busy}
        >
          Save{" "}
          {busy && (
            <Spinner
              as="span"
              animation="grow"
              size="sm"
              role="status"
              aria-hidden="true"
              className="ml-3"
            />
          )}
        </Button>
      </div>
    </div>
  );
};

export default TelehealthSetting;
